import { useNavigate } from 'react-router-dom';
import { useDoctorMedicalRecords } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

export function MedicalRecordsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: records, isLoading, isError } = useDoctorMedicalRecords(user?.id || '');

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="animate-pulse space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container max-w-3xl py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Erro ao carregar prontuários</h2>
          <p className="text-gray-500 mt-2">
            Não foi possível carregar a lista de prontuários.
          </p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    SIGNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };

  const statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Completo',
    SIGNED: 'Assinado',
    CANCELLED: 'Cancelado',
  };

  return (
    <div className="container max-w-4xl py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Meus Prontuários</h1>
        <p className="text-gray-500 mt-1">
          Lista de todos os prontuários que você criou
        </p>
      </div>

      {!records || records.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Nenhum prontuário encontrado</h3>
          <p className="mt-2 text-gray-500">
            Você ainda não criou nenhum prontuário médico.
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Os prontuários são criados automaticamente após as consultas.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/medical-records/${record.id}`)}
            >
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      {new Date(record.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                    <p className="text-lg font-medium text-gray-900 mt-1">
                      Prontuário #{record.id.slice(0, 8)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[record.status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels[record.status] || record.status}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {record.diagnosisIds && record.diagnosisIds.length > 0 && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                      {record.diagnosisIds.length} diagnóstico(s)
                    </span>
                  )}
                  {record.prescriptionIds && record.prescriptionIds.length > 0 && (
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
                      {record.prescriptionIds.length} prescrição(ões)
                    </span>
                  )}
                  {record.examRequestIds && record.examRequestIds.length > 0 && (
                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                      {record.examRequestIds.length} exame(s)
                    </span>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/medical-records/${record.id}`);
                    }}
                  >
                    Ver Prontuário
                  </Button>
                  {record.status !== 'SIGNED' && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/medical-records/${record.id}/edit`);
                      }}
                    >
                      Editar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
